import React, { createElement, Fragment, ReactElement, useCallback } from 'react';
import { useParams } from 'next/navigation';
import { TypographyProps } from './types';

const Typography: React.FC<TypographyProps> = ({
  children,
  className = '',
  translation,
  as = 'p',
  asSkeleton = false,
  ...props
}) => {
  const { locale } = useParams();

  const getContent = useCallback(() => {
    // Check if the children has different locales
    if (typeof children !== 'string') {
      // Update text based on locale
      return children?.[locale as string];
    }

    // Check if there is translation
    if (translation) {
      // Simplified translation handling - just return children for now
      return children;
    }

    return children;
  }, [locale, translation, children]);

  // Constructing default props of the element
  const elementProps = {
    className: `${className} relative`,
    ...props,
  };

  // Handle fragment case separately
  if (as === 'fragment') {
    return (
      <>
        {asSkeleton && <div className="animate-pulse bg-gray-300 h-4 rounded" />}
        {getContent()}
      </>
    );
  }

  // For regular HTML elements, use a switch statement to avoid dynamic typing issues
  const content = (
    <>
      {asSkeleton && <div className="animate-pulse bg-gray-300 h-4 rounded" />}
      {getContent()}
    </>
  );

  switch (as) {
    case 'h1':
      return <h1 {...elementProps}>{content}</h1>;
    case 'h2':
      return <h2 {...elementProps}>{content}</h2>;
    case 'h3':
      return <h3 {...elementProps}>{content}</h3>;
    case 'h4':
      return <h4 {...elementProps}>{content}</h4>;
    case 'h5':
      return <h5 {...elementProps}>{content}</h5>;
    case 'h6':
      return <h6 {...elementProps}>{content}</h6>;
    case 'span':
      return <span {...elementProps}>{content}</span>;
    case 'label':
      return <label {...elementProps}>{content}</label>;
    default:
      return <p {...elementProps}>{content}</p>;
  }
};

export default Typography;
